import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DataTable from '../Common/DataTable';
import DocumentPdfViewer from '../Common/DocumentPdfViewer';
import ReportEmailRequestDialog from './ReportEmailRequestDialog';
import { api } from '../../services/api';
import {
  buildDocumentsPrefilterFilters,
  formatTenantLocationLabel,
} from './reportFieldUtils';
import './Reports.css';

const DOCUMENTS_HIDDEN_COLUMNS_KEY = 'documents-hidden-columns-v2';

const getColumnId = (col, index) => col.columnId || `${col.field}::${col.label ?? index}`;

const normalizeColumns = (columns) =>
  (columns || []).map((col, index) => ({
    ...col,
    columnId: getColumnId(col, index),
  }));

const loadHiddenColumnIds = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DOCUMENTS_HIDDEN_COLUMNS_KEY) || '[]');
    return new Set(Array.isArray(saved) ? saved : []);
  } catch {
    return new Set();
  }
};

const pickRowValue = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const resolveDocumentLinkParams = (col, row) => {
  const linkParams = col?.linkParams || {};
  const dln = pickRowValue(row, [
    linkParams.dln,
    'dln',
    'Dln',
    'DLN',
    'docNum',
    'DocNum',
  ].filter(Boolean));

  const dlnDate = pickRowValue(row, [
    linkParams.dlnDate,
    'dlnDate',
    'DlnDate',
    col?.field,
  ].filter(Boolean));

  return { dln, dlnDate };
};

export default function DocumentsReport({
  user,
  selectedContractId,
  search,
  tableActionsRef,
  onColumnStateChange,
  onToolbarActionsChange,
  documentsPrefilter,
  onDocumentsPrefilterApplied,
}) {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hiddenColumnIds, setHiddenColumnIds] = useState(loadHiddenColumnIds);
  const [fileNotFoundMessage, setFileNotFoundMessage] = useState('');
  const [documentLoading, setDocumentLoading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    title: '',
    pdfUrl: null,
  });
  const [emailRequestOpen, setEmailRequestOpen] = useState(false);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState('');
  const [activePrefilter, setActivePrefilter] = useState(null);
  const [tableInitialFilters, setTableInitialFilters] = useState(null);
  const [tableInitialFiltersKey, setTableInitialFiltersKey] = useState(undefined);
  const pdfUrlRef = useRef(null);
  const emailMessageTimeoutRef = useRef(null);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !hiddenColumnIds.has(col.columnId)),
    [columns, hiddenColumnIds]
  );

  const emailTableColumns = useMemo(
    () => visibleColumns.slice(0, 5),
    [visibleColumns]
  );

  const handleEmailMockSent = useCallback((result) => {
    if (emailMessageTimeoutRef.current) {
      clearTimeout(emailMessageTimeoutRef.current);
    }
    setEmailSuccessMessage(
      `[Mock] נשלחו ${result.itemCount} שורות ל-${result.recipientLabel} (${result.recipientEmail})`
    );
    emailMessageTimeoutRef.current = setTimeout(() => {
      setEmailSuccessMessage('');
      emailMessageTimeoutRef.current = null;
    }, 10000);
  }, []);

  useEffect(() => {
    if (!onToolbarActionsChange) return undefined;

    if (data.length > 0) {
      onToolbarActionsChange({
        onSendEmail: () => setEmailRequestOpen(true),
      });
    } else {
      onToolbarActionsChange(null);
    }

    return () => onToolbarActionsChange(null);
  }, [data.length, onToolbarActionsChange]);

  useEffect(() => {
    localStorage.setItem(
      DOCUMENTS_HIDDEN_COLUMNS_KEY,
      JSON.stringify([...hiddenColumnIds])
    );
  }, [hiddenColumnIds]);

  useEffect(() => {
    setHiddenColumnIds((prev) => {
      const validIds = new Set(columns.map((col) => col.columnId));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [columns]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumnIds((prev) => {
      const next = new Set(prev);
      const visibleCount = columns.filter((col) => !next.has(col.columnId)).length;

      if (!next.has(columnId)) {
        if (visibleCount <= 1) return prev;
        next.add(columnId);
      } else {
        next.delete(columnId);
      }

      return next;
    });
  }, [columns]);

  const showAllColumns = useCallback(() => {
    setHiddenColumnIds(new Set());
  }, []);

  useEffect(() => {
    if (!onColumnStateChange) return undefined;

    if (columns.length === 0) {
      onColumnStateChange(null);
      return () => onColumnStateChange(null);
    }

    onColumnStateChange({
      columns,
      hiddenIds: hiddenColumnIds,
      onToggle: toggleColumnVisibility,
      onShowAll: showAllColumns,
    });

    return () => onColumnStateChange(null);
  }, [
    columns,
    hiddenColumnIds,
    onColumnStateChange,
    toggleColumnVisibility,
    showAllColumns,
  ]);

  const showFileNotFoundMessage = useCallback(() => {
    setFileNotFoundMessage('הקובץ לא נמצא');
  }, []);

  const dismissFileNotFoundMessage = useCallback(() => {
    setFileNotFoundMessage('');
  }, []);

  const closePdfViewer = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfViewer({
      open: false,
      title: '',
      pdfUrl: null,
    });
  }, []);

  useEffect(() => () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
    }
    if (emailMessageTimeoutRef.current) {
      clearTimeout(emailMessageTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!selectedContractId || !user) return;

    setColumns([]);
    setData([]);

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.getDocumentsReport(
          user.userCode,
          user.type,
          selectedContractId
        );

        setColumns(normalizeColumns(response.columns || []));
        setData(response.data || []);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedContractId, user]);

  useEffect(() => {
    if (!documentsPrefilter || columns.length === 0) return;

    const filters = buildDocumentsPrefilterFilters(columns, documentsPrefilter);
    if (Object.keys(filters).length === 0) return;

    setActivePrefilter(documentsPrefilter);
    setTableInitialFilters(filters);
    setTableInitialFiltersKey(
      `${selectedContractId}:${documentsPrefilter.building}:${documentsPrefilter.apartment}:${Date.now()}`
    );
    onDocumentsPrefilterApplied?.();
  }, [documentsPrefilter, columns, selectedContractId, onDocumentsPrefilterApplied]);

  const clearPrefilter = useCallback(() => {
    setActivePrefilter(null);
    setTableInitialFilters({});
    setTableInitialFiltersKey(`cleared:${Date.now()}`);
    tableActionsRef?.current?.setFilters?.({});
  }, [tableActionsRef]);

  const handleLinkClick = async (col, row) => {
    const { dln, dlnDate } = resolveDocumentLinkParams(col, row);

    dismissFileNotFoundMessage();

    if (!dln || !dlnDate) {
      showFileNotFoundMessage();
      return;
    }

    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }

    setDocumentLoading(true);

    try {
      const blob = await api.getDocumentFile(selectedContractId, dln, dlnDate);
      const objectUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = objectUrl;
      setPdfViewer({
        open: true,
        title: `${col.label || 'מסמך'} – ${row[col.field] ?? ''}`,
        pdfUrl: objectUrl,
      });
    } catch {
      showFileNotFoundMessage();
    } finally {
      setDocumentLoading(false);
    }
  };

  if (loading && columns.length === 0) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message">טוען...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message report-state-message--error">
          שגיאה בטעינת הדוח: {error}
        </div>
      </div>
    );
  }

  if (!columns || columns.length === 0) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message">אין עמודות להצגה</div>
      </div>
    );
  }

  return (
    <>
      <div className="report-panel">
        {emailSuccessMessage && (
          <div className="success-message documents-email-success" role="status">
            {emailSuccessMessage}
          </div>
        )}
        {fileNotFoundMessage && (
          <div className="error-message documents-file-error" role="alert">
            <span>{fileNotFoundMessage}</span>
            <button
              type="button"
              className="documents-file-error-dismiss"
              onClick={dismissFileNotFoundMessage}
              aria-label="סגירת הודעה"
            >
              ×
            </button>
          </div>
        )}
        {documentLoading && (
          <div className="documents-file-loading">טוען מסמך...</div>
        )}
        {activePrefilter && (activePrefilter.building || activePrefilter.apartment) && (
          <div className="documents-prefilter-banner">
            <span>
              מסונן לפי {formatTenantLocationLabel(activePrefilter)}
            </span>
            <button type="button" className="documents-prefilter-clear" onClick={clearPrefilter}>
              נקה סינון
            </button>
          </div>
        )}
        <DataTable
          ref={tableActionsRef}
          columns={visibleColumns}
          data={data}
          search={search}
          onLinkClick={handleLinkClick}
          initialFilters={tableInitialFilters}
          initialFiltersKey={tableInitialFiltersKey}
        />
      </div>

      <DocumentPdfViewer
        open={pdfViewer.open}
        title={pdfViewer.title}
        pdfUrl={pdfViewer.pdfUrl}
        onClose={closePdfViewer}
      />

      <ReportEmailRequestDialog
        open={emailRequestOpen}
        onClose={() => setEmailRequestOpen(false)}
        rows={data}
        tableColumns={emailTableColumns}
        contractId={selectedContractId ? String(selectedContractId) : ''}
        onSent={handleEmailMockSent}
      />
    </>
  );
}
